/**
 * Die Farbe der Knöpfe — „Freigeben", „GPX importieren", „Senden".
 *
 * **Chatübergreifend, und das war der Wunsch.** Sie steht bei den allgemeinen
 * Einstellungen und nicht an einer Reise: eine Reise soll nicht anders
 * aussehen als die nächste.
 *
 * **Leer heißt „wie das Erscheinungsbild".** Derselbe Gedanke wie bei den
 * Blasenfarben (`blasenfarben.ts`), und aus demselben Grund: die dunkle
 * Darstellung hat einen eigenen, helleren Akzent. Eine einmal gesetzte helle
 * Farbe gälte sonst auch dort, und ein blasses Grün auf dunklem Grund wäre
 * eine Sackgasse. Deshalb wird die Variable nur gesetzt, wenn wirklich etwas
 * gewählt wurde — sonst wird sie **weggenommen**.
 *
 * **Und was gewählt wurde, wird für die andere Fassung nachgerechnet** (#73).
 * Genau das fehlte: der Satz oben nennt das Problem und behandelt nur den
 * Fall, dass *nichts* eingestellt ist. Aus dem Betrieb, mit Bildschirmfoto:
 * die eingestellte Akzentfarbe `#051947` trug im dunklen Erscheinungsbild den
 * gewählten Eintrag der Einstellungsliste — dunkles Blau auf `#2c2c2d`,
 * gemessen **1,22:1**, während die ungewählten Einträge daneben in Weiß bei
 * 13,95:1 standen. Auf Weiß ist dieselbe Farbe tadellos (16,98:1); sie war nie
 * falsch gewählt, sie stand nur auf dem falschen Grund.
 */

import { FLAECHE_DUNKEL, FLAECHE_HELL, lesbarAuf, schriftAuf } from './kontrast'

/**
 * Die drei Variablen zu einer gewählten Farbe — oder dreimal `null`.
 *
 * - `--accent` ist die Fläche. **Und zugleich eine Schriftfarbe**: der
 *   gewählte Eintrag der Einstellungsliste, ein Verweis, das Zeichen an einer
 *   offenen Zeile. Deshalb muss sie sich gegen den Grund behaupten, auf dem
 *   sie landet — `lesbarAuf()` rückt sie so weit weg, wie es dafür nötig ist,
 *   und lässt sie in Ruhe, wenn sie es ohnehin schafft.
 * - `--accent-text` ist die Schrift darauf, gerechnet und nicht geraten
 *   (`schriftAuf`): wer ein dunkles Blau wählt, bekäme sonst dunkle Schrift
 *   darauf und läse nichts. **Gerechnet wird sie aus der angepassten Farbe**,
 *   nicht aus der eingestellten — sonst stünde sie auf einem anderen Ton, als
 *   sie gemeint war.
 * - `--accent-soft` ist der blasse Grund hinter Hinweisen und aktiven
 *   Reitern. **Als `rgba` und nicht als aufgehellte Farbe**: durchsichtig
 *   mischt sie sich mit dem Grund, auf dem sie liegt, und passt damit hell
 *   wie dunkel. Eine zu Weiß hin aufgehellte Fassung wäre in der dunklen
 *   Darstellung ein heller Fleck.
 */
export function knopfVariablen(
  farbe: string,
  dunkel = false,
): Record<string, string | null> {
  if (!/^#[0-9a-fA-F]{6}$/.test(farbe)) {
    return { '--accent': null, '--accent-text': null, '--accent-soft': null }
  }

  const gilt = lesbarAuf(farbe, dunkel ? FLAECHE_DUNKEL : FLAECHE_HELL)

  const zahl = Number.parseInt(gilt.slice(1), 16)
  const r = (zahl >> 16) & 255
  const g = (zahl >> 8) & 255
  const b = zahl & 255

  return {
    '--accent': gilt,
    '--accent-text': schriftAuf(gilt),
    '--accent-soft': `rgba(${r}, ${g}, ${b}, 0.18)`,
  }
}
